/**
 * The four arrival forms as URL slugs.
 *
 * The slug is in the path (`/arrivals/pdn/12`) rather than a query parameter so the side rail
 * highlights exactly one entry: the nav matches on `pathname.startsWith`, and a single
 * `/arrivals?form=` route would light all four at once.
 */
import { FORMS, type HeaderFieldKey } from "@nexseed/shared";
import {
  Boxes,
  Flame,
  PackageCheck,
  ScanLine,
  Sprout,
  Truck,
  type LucideIcon,
} from "lucide-react";

export const FORM_BY_SLUG = {
  pdn: "arrival_pdn",
  "s-capture": "arrival_scapture",
  trading: "arrival_trading",
  "stock-transfer": "arrival_stock_transfer",
  // Only the HEAD of the cob chain gets a slug. Its four stage forms are reached through it and
  // are never URL-addressable, which is also what keeps one truck reading as one row in the list.
  cob: "arrival_cob",
  "cob-receipt": "arrival_cob_receipt",
} as const;

export type FormSlug = keyof typeof FORM_BY_SLUG;
export const FORM_SLUGS = Object.keys(FORM_BY_SLUG) as FormSlug[];

export const SLUG_BY_FORM = Object.fromEntries(
  Object.entries(FORM_BY_SLUG).map(([slug, code]) => [code, slug])
) as Record<string, FormSlug>;

export const isFormSlug = (v: string): v is FormSlug => v in FORM_BY_SLUG;
export const formCodeOf = (slug: FormSlug) => FORM_BY_SLUG[slug];
/** seedtrac's own menu wording, straight off the form config. */
export const formTitle = (slug: FormSlug) => FORMS[FORM_BY_SLUG[slug]]!.title;

export const FORM_ICON: Record<FormSlug, LucideIcon> = {
  pdn: Sprout,
  "s-capture": ScanLine,
  trading: Truck,
  "stock-transfer": Boxes,
  cob: Flame,
  "cob-receipt": PackageCheck,
};

/**
 * Every cob form resolves back to the chain's own slug.
 *
 * `SLUG_BY_FORM` is built by inverting `FORM_BY_SLUG`, so a STAGE form code is not in it — and
 * `arrival-screen` reads that map when it redirects after a create and after a cancel. Without
 * this fallback those redirects would build `/arrivals/undefined/12`.
 */
const CHAIN_HEAD: Record<string, FormSlug> = {
  arrival_cob: "cob",
  cob_blend: "cob",
  cob_drying: "cob",
  cob_dispatch: "cob",
};

export const slugOf = (formCode: string): FormSlug =>
  SLUG_BY_FORM[formCode] ?? CHAIN_HEAD[formCode] ?? "pdn";

/**
 * Forms whose Post Item Form opens with the lot's PDN particulars folded under a one-line summary
 * (user, 2026-09-19): S-Capture's come off the feed, locked, so the desk reads them once rather than
 * working down eleven grey boxes to reach the weights. Data, like `FORM_SCREEN`, so no component
 * branches on a form code.
 */
export const COLLAPSED_LOT_DETAILS: Record<FormSlug, boolean> = {
  pdn: false,
  "s-capture": true,
  trading: false,
  "stock-transfer": false,
  cob: false,
  "cob-receipt": false,
};

/**
 * Label/control pairs to a row in the arrival header (the desk details). Trading's header is long
 * -- challan, party, crop, variety, vendor variety, lots, stage, then transport -- so it packs three
 * to a row (user, 2026-09-19); the rest keep seedtrac's two.
 */
export const HEADER_COLUMNS: Record<FormSlug, 2 | 3> = {
  pdn: 2,
  "s-capture": 2,
  trading: 2,
  "stock-transfer": 2,
  cob: 2,
  "cob-receipt": 2,
};

/**
 * Header fields the desk reads as one fact, drawn on one line of three (user, 2026-09-19): what a
 * Trading arrival IS -- its crop, the variety, and the vendor's own name for that variety.
 */
export const HEADER_ROW_GROUPS: Partial<
  Record<FormSlug, readonly (readonly HeaderFieldKey[])[]>
> = {
  trading: [["header_crop", "header_variety", "header_vendor_variety"]],
};

/**
 * How a new arrival travels until the desk says otherwise (user, 2026-09-19): Trading's seed comes
 * by transport, so Mode of Transit starts there and its Transport rows show at once.
 */
export const DEFAULT_TRANSIT_MODE: Partial<
  Record<FormSlug, "transport" | "courier" | "hand_delivery">
> = {
  trading: "transport",
};

/**
 * Forms whose sheet is split into two steps (user, 2026-09-19): 1 Desk details -- the header and
 * Remarks -- and 2 Lots & SLOC -- the lot table and the Post Item Form. Trading's header is long
 * enough that the desk fills it as a step of its own; the other forms stay one page.
 */
export const SHEET_STEPS: Record<FormSlug, boolean> = {
  pdn: false,
  "s-capture": false,
  trading: true,
  "stock-transfer": false,
  cob: false,
  "cob-receipt": false,
};

/**
 * The Stage a lot starts at on forms that receive seed straight off the field or the dryer (user,
 * 2026-09-19): PDN, S-Capture and Maize-Dried Seed Arrival are Raw unless the desk says otherwise.
 * A stage CODE, resolved against the stage master, never a hard-coded id.
 */
export const DEFAULT_LINE_STAGE: Partial<Record<FormSlug, string>> = {
  pdn: "R",
  "s-capture": "R",
  "cob-receipt": "R",
};

/**
 * What the Pending tab lists: the form's own draft documents, or the trucks waiting to be booked.
 *
 * - `scapture-trucks` — an S-Capture dispatch lands in `pdn_inbox` from the feed before any document
 *   exists, so until someone books it there is no draft to list. Listing drafts there showed what the
 *   desk had already started instead of what was waiting at the plant.
 * - `incoming-transfers` — a Stock Transfer is on the road here from the moment the sending plant's
 *   gate passes it out, before any receipt exists, so its Pending tab lists those transfers.
 * - `documents` — every other form's work begins when an operator raises it, so its drafts ARE its
 *   pending queue.
 *
 * Stated here as data for the same reason as `FORM_SCREEN`: no component branches on a form code.
 */
export type PendingView =
  "documents" | "scapture-trucks" | "incoming-transfers" | "incoming-cob";

export const FORM_PENDING: Record<FormSlug, PendingView> = {
  pdn: "documents",
  "s-capture": "scapture-trucks",
  trading: "documents",
  "stock-transfer": "incoming-transfers",
  cob: "documents",
  "cob-receipt": "incoming-cob",
};
